import { reactive, ref, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { parseQueryString } from '@/helpers'

/**
 * Unified Filter State Manager
 * 
 * Centralizes all filter state management to prevent conflicts and circular updates.
 * Provides single source of truth for filter state across FilterBar, Products page, and WithFilter.
 */
export const useFilterManager = (options = {}) => {
  const route = useRoute()
  const router = useRouter()
  
  // Configuration
  const config = {
    debounceMs: options.debounceMs || 200,
    enableUrlSync: options.enableUrlSync !== false,
    portalPage: options.portalPage || 'products', // 'products' or 'search'
    ...options
  }

  // Internal flags to prevent circular updates
  const _isUpdatingFromUrl = ref(false)
  const _isUpdatingFromInternal = ref(false)
  const _isInitializing = ref(true)
  const _updateTimeout = ref(null)
  const _subscribers = ref([])

  // External restoration manager reference (set via setRestorationManager)
  let restorationManager = null
  
  // Helper to safely check if restoration is in progress
  const isRestorationInProgress = () => {
    return restorationManager && 
           restorationManager.isRestoring && 
           typeof restorationManager.isRestoring.value !== 'undefined' &&
           restorationManager.isRestoring.value === true
  }

  /**
   * Unified filter state - single source of truth
   */
  const filterState = reactive({
    // Category
    category_id: null,
    main_category_id: null,
    sub_category_id: null,
    
    // Search
    search: '',
    
    // Attributes
    attribute_ids: [],
    
    // Sorting
    sort_by: '',
    sort_type: '',
    sort_by_force: '',
    userHasSelectedSorting: false,
    
    // Product type and rating
    product_type: '',
    rating: '',
    
    // Price range
    min_price: null,
    max_price: null,
    
    // Space range
    min_space: null,
    max_space: null,
    
    // Location
    governorate_id: null,
    area_id: null,
    store_id: null,
    
    // Negotiable
    is_negotiable: true,
    
    // Query string representation (for backward compatibility)
    filterQuery: '',
    attributesQuery: '',
  })

  /**
   * Build query string from filter state
   */
  const buildQueryString = (state = filterState) => {
    const parts = []
    
    // Category - prefer selected category (category_id from UI), then sub_category_id, then main_category_id
    let categoryIdToUse = null
    if (state.category_id && state.category_id !== '0' && state.category_id !== 0) {
      categoryIdToUse = state.category_id
    } else if (state.sub_category_id) {
      categoryIdToUse = state.sub_category_id
    } else if (state.main_category_id) {
      categoryIdToUse = state.main_category_id
    }

    if (categoryIdToUse) {
      parts.push(`category_id=${encodeURIComponent(categoryIdToUse)}`)
      if (state.main_category_id && String(state.main_category_id) !== String(categoryIdToUse)) {
        parts.push(`main_category_id=${encodeURIComponent(state.main_category_id)}`)
      }
    }
    
    // Search (only for search page)
    if (config.portalPage === 'search' && state.search) {
      parts.push(`search=${encodeURIComponent(state.search)}`)
    }
    
    // Attributes
    if (state.attribute_ids && Array.isArray(state.attribute_ids) && state.attribute_ids.length > 0) {
      state.attribute_ids.forEach(id => {
        parts.push(`attribute_ids[]=${encodeURIComponent(id)}`)
      })
    }
    
    // Sorting
    if (state.userHasSelectedSorting) {
      if (state.sort_by && state.sort_by !== 'date') {
        parts.push(`sort_by=${encodeURIComponent(state.sort_by)}`)
      }
      if (state.sort_type && state.sort_type !== 'desc') {
        parts.push(`sort_type=${encodeURIComponent(state.sort_type)}`)
      }
    }
    
    // Product type
    if (state.product_type) {
      parts.push(`product_type=${encodeURIComponent(state.product_type)}`)
    }
    
    // Rating
    if (state.rating) {
      parts.push(`rating=${encodeURIComponent(state.rating)}`)
    }
    
    // Price range
    if (state.min_price) {
      parts.push(`min_price=${encodeURIComponent(state.min_price)}`)
    }
    if (state.max_price) {
      parts.push(`max_price=${encodeURIComponent(state.max_price)}`)
    }
    
    // Space range
    if (state.min_space) {
      parts.push(`min_space=${encodeURIComponent(state.min_space)}`)
    }
    if (state.max_space) {
      parts.push(`max_space=${encodeURIComponent(state.max_space)}`)
    }
    
    // Location
    if (state.governorate_id) {
      parts.push(`governorate_id=${encodeURIComponent(state.governorate_id)}`)
    }
    if (state.area_id) {
      parts.push(`area_id=${encodeURIComponent(state.area_id)}`)
    }
    if (state.store_id) {
      parts.push(`store_id=${encodeURIComponent(state.store_id)}`)
    }
    
    // Negotiable (only add if false)
    if (state.is_negotiable === false || state.is_negotiable === 0 || state.is_negotiable === '0') {
      parts.push(`is_negotiable=0`)
    }
    
    // Sort by force (verified)
    if (state.sort_by_force) {
      parts.push(`sort_by_force=${encodeURIComponent(state.sort_by_force)}`)
    }
    
    return parts.join('&')
  }

  /**
   * Parse query string into filter state
   */
  const parseQueryStringToState = (queryString) => {
    if (!queryString) return {}
    
    try {
      // Try base64 decode first
      let decodedQuery
      try {
        decodedQuery = decodeURIComponent(atob(queryString))
      } catch {
        decodedQuery = queryString
      }
      
      const parsed = parseQueryString(decodedQuery)
      const state = {}
      
      // Category - prioritize category_id (sub-category) over main_category_id
      // This ensures sub-category ID is used in API requests when both are present
      if (parsed.category_id) {
        state.category_id = parsed.category_id
        // If main_category_id also exists, store it for reference but don't override category_id
        if (parsed.main_category_id) {
          state.main_category_id = parsed.main_category_id
        }
      } else if (parsed.main_category_id) {
        state.main_category_id = parsed.main_category_id
        state.category_id = parsed.main_category_id
      }
      
      // Search
      if (parsed.search) {
        state.search = parsed.search
      }
      
      // Attributes
      if (parsed.attribute_ids) {
        if (Array.isArray(parsed.attribute_ids)) {
          state.attribute_ids = parsed.attribute_ids.map(id => String(id))
        } else {
          state.attribute_ids = [String(parsed.attribute_ids)]
        }
      }
      
      // Sorting
      if (parsed.sort_by) {
        state.sort_by = parsed.sort_by
        state.userHasSelectedSorting = true
      }
      if (parsed.sort_type) {
        state.sort_type = parsed.sort_type
      }
      
      // Product type
      if (parsed.product_type) {
        state.product_type = parsed.product_type
      }
      
      // Rating
      if (parsed.rating) {
        state.rating = parsed.rating
      }
      
      // Price
      if (parsed.min_price) {
        state.min_price = parseInt(parsed.min_price, 10)
      }
      if (parsed.max_price) {
        state.max_price = parseInt(parsed.max_price, 10)
      }
      
      // Space
      if (parsed.min_space) {
        state.min_space = parseInt(parsed.min_space, 10)
      }
      if (parsed.max_space) {
        state.max_space = parseInt(parsed.max_space, 10)
      }
      
      // Location
      if (parsed.governorate_id) {
        state.governorate_id = parsed.governorate_id
      }
      if (parsed.area_id) {
        state.area_id = parsed.area_id
      }
      if (parsed.store_id) {
        state.store_id = parsed.store_id
      }
      
      // Negotiable
      if (parsed.is_negotiable !== undefined) {
        state.is_negotiable = parsed.is_negotiable === '1' || parsed.is_negotiable === 1 || parsed.is_negotiable === 'true' || parsed.is_negotiable === true
      }
      
      // Sort by force
      if (parsed.sort_by_force) {
        state.sort_by_force = parsed.sort_by_force
      }
      
      return state
    } catch (error) {
      console.error('Error parsing query string:', error)
      return {}
    }
  }

  /**
   * Update filter state from any source
   * @param {string} source - Source identifier ('filterbar', 'products', 'url', etc.)
   * @param {Object} updates - Partial filter state updates
   * @param {Object} options - Update options
   */
  const updateFilterState = (source, updates = {}, options = {}) => {
    // Check if restoration is in progress and should block updates
    if (isRestorationInProgress() && !options.allowDuringRestoration) {
      console.log(`⏸️ Filter update blocked - restoration in progress (source: ${source})`)
      return
    }
    
    // Prevent circular updates
    if (_isUpdatingFromUrl.value && source !== 'url') {
      return
    }
    
    // Merge updates into filter state
    Object.keys(updates).forEach(key => {
      if (key in filterState) {
        const newValue = updates[key]
        const oldValue = filterState[key]
        
        // Handle arrays specially
        if (Array.isArray(newValue) && Array.isArray(oldValue)) {
          const newSorted = [...newValue].sort().join(',')
          const oldSorted = [...oldValue].sort().join(',')
          if (newSorted !== oldSorted) {
            filterState[key] = [...newValue]
          }
        } else if (newValue !== oldValue) {
          filterState[key] = newValue
        }
      }
    })
    
    // Update derived query strings for backward compatibility
    const queryString = buildQueryString(filterState)
    filterState.filterQuery = queryString
    
    // Build attributes query
    if (filterState.attribute_ids && filterState.attribute_ids.length > 0) {
      filterState.attributesQuery = filterState.attribute_ids.join(',')
    } else {
      filterState.attributesQuery = ''
    }
    
    // Sync to URL if enabled and not updating from URL
    // Don't sync during restoration or initialization to preserve existing URL
    if (config.enableUrlSync && 
        !_isUpdatingFromUrl.value && 
        !options.skipUrlSync &&
        !isRestorationInProgress() &&
        !_isInitializing.value) {
      syncToUrl()
    }
    
    // Notify subscribers (debounced)
    if (!options.skipNotification) {
      clearTimeout(_updateTimeout.value)
      _updateTimeout.value = setTimeout(() => {
        notifySubscribers(source, { ...filterState })
      }, config.debounceMs)
    }
  }

  /**
   * Sync current filter state to URL
   */
  const syncToUrl = () => {
    if (process.server || !config.enableUrlSync) return
    
    // Don't clear URL during restoration - preserve existing query params
    if (isRestorationInProgress()) {
      console.log('⏸️ Skipping URL sync during restoration');
      return;
    }
    
    try {
      const queryString = buildQueryString(filterState)
      
      // Build full URL
      const currentPath = route.path
      let newPath = currentPath
      
      if (queryString) {
        const encodedQuery = btoa(encodeURIComponent(queryString))
        newPath = `${currentPath}?${encodedQuery}`
      }
      
      // Only update if different
      const currentQuery = route.fullPath.split('?')[1] || ''
      const newQuery = queryString ? btoa(encodeURIComponent(queryString)) : ''
      
      // Don't clear URL if we have existing query params but new query is empty
      // This prevents clearing filters during restoration
      if (currentQuery && !newQuery && _isInitializing.value) {
        console.log('⏸️ Skipping URL sync - would clear existing query during initialization');
        return;
      }
      
      if (currentQuery !== newQuery) {
        _isUpdatingFromInternal.value = true
        router.replace(newPath, { silent: true }).catch(() => {
          // Ignore navigation errors
        }).finally(() => {
          setTimeout(() => {
            _isUpdatingFromInternal.value = false
          }, 100)
        })
      }
    } catch (error) {
      console.error('Error syncing to URL:', error)
    }
  }

  /**
   * Restore filter state from URL
   */
  const restoreFromUrl = () => {
    if (process.server) return
    
    try {
      const queryString = route.fullPath.split('?')[1]
      if (!queryString) {
        // No query params - reset to defaults
        resetFilters({ skipNotification: true, skipUrlSync: true })
        return
      }
      
      const parsedState = parseQueryStringToState(queryString)
      
      if (Object.keys(parsedState).length > 0) {
        _isUpdatingFromUrl.value = true
        updateFilterState('url', parsedState, { skipUrlSync: true, skipNotification: false })
        setTimeout(() => {
          _isUpdatingFromUrl.value = false
        }, 100)
      }
    } catch (error) {
      console.error('Error restoring from URL:', error)
    }
  }

  /**
   * Reset all filters to defaults
   */
  const resetFilters = (options = {}) => {
    const defaults = {
      category_id: null,
      main_category_id: null,
      sub_category_id: null,
      search: '',
      attribute_ids: [],
      sort_by: '',
      sort_type: '',
      sort_by_force: '',
      userHasSelectedSorting: false,
      product_type: '',
      rating: '',
      min_price: null,
      max_price: null,
      min_space: null,
      max_space: null,
      governorate_id: null,
      area_id: null,
      store_id: null,
      is_negotiable: true,
      filterQuery: '',
      attributesQuery: '',
    }
    
    Object.keys(defaults).forEach(key => {
      filterState[key] = defaults[key]
    })
    
    if (!options.skipUrlSync && config.enableUrlSync) {
      syncToUrl()
    }
    
    if (!options.skipNotification) {
      notifySubscribers('reset', { ...filterState })
    }
  }

  /**
   * Subscribe to filter state changes
   */
  const subscribeToChanges = (callback) => {
    if (typeof callback !== 'function') return
    
    _subscribers.value.push(callback)
    
    // Return unsubscribe function
    return () => {
      const index = _subscribers.value.indexOf(callback)
      if (index > -1) {
        _subscribers.value.splice(index, 1)
      }
    }
  }

  /**
   * Notify all subscribers of filter state change
   */
  const notifySubscribers = (source, state) => {
    _subscribers.value.forEach(callback => {
      try {
        callback(state, source)
      } catch (error) {
        console.error('Error in filter state subscriber:', error)
      }
    })
  }

  /**
   * Set restoration manager reference
   */
  const setRestorationManager = (manager) => {
    restorationManager = manager
  }

  /**
   * Get current filter state (read-only copy)
   */
  const getFilterState = () => {
    return { ...filterState }
  }

  /**
   * Mark initialization as complete
   */
  const markInitialized = () => {
    _isInitializing.value = false
  }

  /**
   * Check if manager is initializing
   */
  const isInitializing = computed(() => _isInitializing.value)

  // Watch route changes to restore from URL
  if (config.enableUrlSync && process.client) {
    watch(() => route.fullPath, (newPath, oldPath) => {
      // Only restore if path changed or query changed (not internal update)
      if (!_isUpdatingFromInternal.value && (newPath !== oldPath)) {
        const pathChanged = route.path !== (oldPath ? oldPath.split('?')[0] : '')
        if (pathChanged || !_isUpdatingFromUrl.value) {
          restoreFromUrl()
        }
      }
    }, { immediate: false })
  }

  return {
    // State
    filterState,
    isInitializing,
    
    // Methods
    updateFilterState,
    resetFilters,
    restoreFromUrl,
    syncToUrl,
    subscribeToChanges,
    setRestorationManager,
    getFilterState,
    markInitialized,
    buildQueryString,
    parseQueryStringToState,
  }
}

